import React from 'react';
import { ArrowLeft, Phone, Calendar, Clock, CheckCircle } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';

const BatterieiPhoneSaintChamond = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <button 
            onClick={() => navigate('/blog')}
            className="flex items-center gap-2 text-gray-700 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Retour au blog</span>
          </button>
          
          <div className="flex items-center gap-4">
            <a href="tel:0773601521" className="flex items-center gap-2 text-gray-700 hover:text-gray-900 transition-colors">
              <Phone className="w-4 h-4" />
              <span className="font-medium">07.73.60.15.21</span>
            </a>
          </div>
        </div>
      </nav>

      {/* Article */}
      <article className="max-w-4xl mx-auto px-4 py-12">
        {/* Meta */}
        <div className="flex items-center gap-4 text-sm text-gray-500 mb-6">
          <span className="flex items-center gap-1">
            <Calendar className="w-4 h-4" />
            10 avril 2026
          </span>
          <span className="flex items-center gap-1">
            <Clock className="w-4 h-4" />
            3 min de lecture
          </span>
        </div>

        {/* Title */}
        <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
          Pourquoi changer sa batterie d'iPhone à Saint-Chamond plutôt que de racheter un téléphone ?
        </h1>

        {/* Featured Image */}
        <img 
          src="https://images.unsplash.com/photo-1556656793-08538906a9f8?w=1200&q=80" 
          alt="Changement batterie iPhone à Saint-Chamond - Réparation par Suro Phone"
          className="w-full h-96 object-cover rounded-lg mb-8"
        />

        {/* Content */}
        <div className="prose prose-lg max-w-none">
          <p className="text-xl text-gray-700 leading-relaxed mb-6">
            Votre iPhone tient de moins en moins la charge ? Avant de vous précipiter pour acheter un nouveau téléphone, saviez-vous qu'un simple <strong>remplacement de batterie</strong> peut redonner une seconde vie à votre appareil ? Chez <strong>Suro Phone à Saint-Chamond</strong>, nous vous expliquons pourquoi c'est la meilleure décision.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">💰 L'aspect économique : Un choix malin</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            Un iPhone neuf représente un investissement important, surtout pour les modèles Pro. En comparaison, changer la batterie de votre iPhone chez Suro Phone ne représente qu'une <strong>petite fraction de ce prix</strong>. C'est une différence considérable !
          </p>
          
          <div className="bg-blue-50 border-l-4 border-blue-500 p-6 my-6 rounded-r-lg">
            <p className="text-lg font-semibold text-gray-900 mb-2">Exemple concret :</p>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
                <span><strong>iPhone neuf</strong> : plusieurs centaines d'euros</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                <span><strong>Changement de batterie</strong> : une fraction du prix, devis gratuit</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                <span><strong>Économie réalisée</strong> : plusieurs centaines d'euros !</span>
              </li>
            </ul>
          </div>

          <p className="text-gray-700 leading-relaxed mb-6">
            Pour moins de <strong>20% du prix d'un iPhone neuf</strong>, vous retrouvez une autonomie maximale et conservez toutes vos données, applications et réglages.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">🌱 L'aspect écologique : Un geste pour la planète</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            Chaque année, des millions de smartphones sont jetés alors qu'ils pourraient être réparés. En changeant simplement votre batterie, vous participez à la <strong>réduction des déchets électroniques</strong>.
          </p>
          <p className="text-gray-700 leading-relaxed mb-6">
            Un iPhone contient des métaux rares et précieux dont l'extraction a un impact environnemental majeur. <strong>Réparer plutôt que jeter</strong>, c'est un geste écologique concret et responsable.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">⚡ La rapidité du service chez Suro Phone</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            Chez <strong>Suro Phone à Saint-Chamond</strong>, le remplacement de votre batterie d'iPhone prend seulement <strong>30 minutes à 1 heure</strong>. Vous repartez le jour même avec votre téléphone comme neuf !
          </p>
          
          <div className="bg-green-50 border-l-4 border-green-500 p-6 my-6 rounded-r-lg">
            <p className="text-lg font-semibold text-gray-900 mb-3">Nos avantages :</p>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                <span><strong>Intervention rapide</strong> : 30 minutes seulement</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                <span><strong>Batteries Premium</strong> : Testées et garanties 3 mois</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                <span><strong>Devis gratuit</strong> : tarif communiqué immédiatement selon votre modèle</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                <span><strong>À Saint-Chamond</strong> : 40 rue de la République</span>
              </li>
            </ul>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">✅ Conclusion</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            Changer la batterie de votre iPhone à Saint-Chamond est un choix <strong>économique, écologique et rapide</strong>. Pour une fraction du prix d'un téléphone neuf, vous retrouvez une autonomie complète tout en réduisant votre impact environnemental.
          </p>
          <p className="text-gray-700 leading-relaxed mb-6">
            <strong>Suro Phone</strong> est votre partenaire de confiance à Saint-Chamond pour redonner vie à votre iPhone. Contactez-nous dès maintenant !
          </p>
        </div>

        {/* CTA */}
        <div className="mt-12 bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-8 text-white text-center">
          <h3 className="text-2xl font-bold mb-4">Besoin de changer votre batterie ?</h3>
          <p className="text-lg mb-6">Contactez Suro Phone dès maintenant pour un devis gratuit</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:0773601521">
              <Button className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-4 text-lg font-semibold">
                <Phone className="w-5 h-5 mr-2" />
                07.73.60.15.21
              </Button>
            </a>
            <Link to="/rendez-vous">
              <Button className="bg-green-500 hover:bg-green-600 text-white px-8 py-4 text-lg font-semibold">
                Prendre rendez-vous
              </Button>
            </Link>
          </div>
        </div>

        {/* Navigation */}
        <div className="mt-12 pt-8 border-t">
          <Link to="/blog" className="text-blue-600 hover:underline font-semibold">
            ← Retour au blog
          </Link>
        </div>
      </article>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 px-4 mt-16">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-gray-400">© 2026 Suro Phone - Blog</p>
        </div>
      </footer>
    </div>
  );
};

export default BatterieiPhoneSaintChamond;